import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, Users, Download, Upload } from 'lucide-react';
import { useCustomerStore } from '../../store/useCustomerStore';
import { useObjectStore } from '../../store/useObjectStore';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/Shared/Button';
import { Modal, ModalFooter } from '../../components/Shared/Modal';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver';
import { FormField } from '../../components/Shared/FormField';
import { TextArea } from '../../components/Shared/TextArea';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { ExcelImportModal } from '../../components/Shared/ExcelImportModal';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlListState } from '../../hooks/useUrlListState';
import { useExcelIo } from '../../hooks/useExcelIo';
import { filterCustomers, SORT_IDS, TAB_IDS, type SortId, type TabId } from './logic/customerList';

import { ListPageShell } from '../../components/Lists/ListPageShell';
import { KpiStripList } from '../../components/Lists/KpiStripList';
import { TabStrip } from '../../components/Lists/TabStrip';
import { ListToolbar } from '../../components/Lists/ListToolbar';
import { ListGrid } from '../../components/Lists/ListGrid';
import { StatusDotInline } from '../../components/Lists/StatusDotInline';

import type { FacilityFlowCoreModulesCustomersDtosCustomerListDto } from '../../api/generated/models';
import type { KpiItem, ListStatus } from '../../types/lists';

type CustomerListDto = FacilityFlowCoreModulesCustomersDtosCustomerListDto;

interface CustomerFormData {
  name: string;
  customerNumber: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
  vatId: string;
  externalNotes: string;
  internalNotes: string;
  status: 'active' | 'inactive';
}

const initialFormData: CustomerFormData = {
  name: '',
  customerNumber: '',
  street: '',
  city: '',
  postalCode: '',
  country: 'Deutschland',
  vatId: '',
  externalNotes: '',
  internalNotes: '',
  status: 'active',
};

const GRID_COLUMNS = '110px minmax(0,1.4fr) minmax(0,1fr) 90px 100px 110px 60px';

export function Customers() {
  const { t } = useTranslation(['customers', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    customers,
    loading,
    saving,
    error,
    fetchAllCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  } = useCustomerStore();
  const { objects, allObjectsLoaded, fetchAllObjects } = useObjectStore();
  const { exporting, exportExcel } = useExcelIo('customers');

  const validationRules = {
    name: { required: true, message: t('customers:validation.nameRequired') },
    street: { required: true, message: t('customers:validation.streetRequired') },
    city: { required: true, message: t('customers:validation.cityRequired') },
    postalCode: {
      required: true,
      pattern: /^\d{4,}$/,
      message: t('customers:validation.postalCodeInvalid'),
    },
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerListDto | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [objectsRefreshed, setObjectsRefreshed] = useState(false);

  const [listState, setListState] = useUrlListState(
    { q: '', tab: 'all' as TabId, sort: 'name' as SortId, page: 1 },
    { tab: TAB_IDS, sort: SORT_IDS },
  );
  const { q: search, tab, sort: sortBy, page: activePage } = listState;

  const createForm = useFormValidation<CustomerFormData>(initialFormData, validationRules);
  const editForm = useFormValidation<CustomerFormData>(initialFormData, validationRules);

  useEffect(() => {
    fetchAllCustomers().then(() => setInitialLoadComplete(true));
    fetchAllObjects().then(() => setObjectsRefreshed(true));
  }, [fetchAllCustomers, fetchAllObjects]);

  // why: object counts and the "objects" sort come from the shared object store. A complete list
  // from an earlier visit is used right away; one another page left filtered is waited out.
  const objectCountsReady = objectsRefreshed || allObjectsLoaded;

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && customers.length > 0) {
      const customer = customers.find((c) => c.id === editId);
      if (customer) {
        openEditModal(customer);
        // Drop only the deep-link param so the list state in the URL survives.
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        navigate({ pathname: '/customers', search: next.toString() }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, customers]);

  const objectCountByCustomer = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of objects) {
      if (!o.customerId) continue;
      map.set(o.customerId, (map.get(o.customerId) ?? 0) + 1);
    }
    return map;
  }, [objects]);

  const totalObjects = objects.length;

  const kpis: KpiItem[] = useMemo(() => {
    const activeCount = customers.filter((c) => c.status === 'active').length;
    const totalCustomers = customers.length;
    return [
      {
        label: t('customers:kpis.active'),
        value: activeCount,
        sub: t('customers:kpis.ofTotal', { count: totalCustomers }),
      },
      {
        label: t('customers:kpis.objectsTotal'),
        value: totalObjects,
      },
    ];
  }, [customers, totalObjects, t]);

  const tabs = useMemo(
    () => [
      { id: 'all', label: t('customers:tabs.all'), count: customers.length },
      {
        id: 'active',
        label: t('customers:tabs.active'),
        count: customers.filter((c) => c.status === 'active').length,
      },
      {
        id: 'inactive',
        label: t('customers:tabs.inactive'),
        count: customers.filter((c) => c.status !== 'active').length,
      },
      {
        id: 'noObjects',
        label: t('customers:tabs.noObjects'),
        count: customers.filter((c) => (objectCountByCustomer.get(c.id ?? '') ?? 0) === 0).length,
        tone: 'warn' as const,
      },
    ],
    [customers, objectCountByCustomer, t],
  );

  const sortOptions = useMemo(
    () => [
      { id: 'name' as const, label: t('customers:sort.name') },
      { id: 'objects' as const, label: t('customers:sort.objects') },
    ],
    [t],
  );

  const filteredCustomers = useMemo(
    () => filterCustomers(customers, { tab, search, sortBy }, objectCountByCustomer),
    [customers, tab, search, sortBy, objectCountByCustomer],
  );

  const { page, pageCount, pageItems, goToPage } = usePagedList(filteredCustomers, undefined, [
    activePage,
    (next) => setListState({ page: next }),
  ]);

  // Export follows the filter, so the file mirrors what the list shows. Undefined when
  // nothing is filtered out — the server then exports everything without a huge ID list.
  const exportIds = useMemo(
    () =>
      filteredCustomers.length === customers.length
        ? undefined
        : filteredCustomers.map((c) => c.id).filter((id): id is string => !!id),
    [filteredCustomers, customers.length],
  );

  function mapListStatus(s: string | null | undefined): ListStatus {
    return s === 'active' ? 'active' : 'inactive';
  }

  const handleCreate = async () => {
    if (!createForm.validateAll()) return;
    try {
      await createCustomer({
        name: createForm.values.name,
        billingAddress: {
          street: createForm.values.street,
          city: createForm.values.city,
          postalCode: createForm.values.postalCode,
          country: createForm.values.country,
        },
        vatId: createForm.values.vatId || null,
        externalNotes: createForm.values.externalNotes,
        internalNotes: createForm.values.internalNotes,
      });
      createForm.reset();
      setIsCreateModalOpen(false);
    } catch (err) {
      console.error('Failed to create customer:', err);
    }
  };

  const handleEdit = async () => {
    if (!selectedCustomer?.id) return;
    if (!editForm.validateAll()) return;
    try {
      await updateCustomer(selectedCustomer.id, {
        name: editForm.values.name,
        billingAddress: {
          street: editForm.values.street,
          city: editForm.values.city,
          postalCode: editForm.values.postalCode,
          country: editForm.values.country,
        },
        vatId: editForm.values.vatId || null,
        externalNotes: editForm.values.externalNotes,
        internalNotes: editForm.values.internalNotes,
      });
      editForm.reset();
      setSelectedCustomer(null);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update customer:', err);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer?.id) return;
    try {
      await deleteCustomer(selectedCustomer.id);
      setIsDeleteModalOpen(false);
      setSelectedCustomer(null);
    } catch (err) {
      console.error('Failed to delete customer:', err);
    }
  };

  async function openEditModal(customer: CustomerListDto) {
    if (!customer.id) return;
    setSelectedCustomer(customer);
    const fullCustomer = await getCustomerById(customer.id);
    if (fullCustomer) {
      editForm.setFormValues({
        name: fullCustomer.name || '',
        customerNumber: fullCustomer.customerNumber || '',
        street: fullCustomer.billingAddress?.street || '',
        city: fullCustomer.billingAddress?.city || '',
        postalCode: fullCustomer.billingAddress?.postalCode || '',
        country: fullCustomer.billingAddress?.country || 'Deutschland',
        vatId: fullCustomer.vatId || '',
        externalNotes: fullCustomer.externalNotes || '',
        internalNotes: fullCustomer.internalNotes || '',
        status: fullCustomer.status === 'active' ? 'active' : 'inactive',
      });
      setIsEditModalOpen(true);
    }
  }

  function openDeleteModal(customer: CustomerListDto) {
    setSelectedCustomer(customer);
    setIsDeleteModalOpen(true);
  }

  // Loading / error state at first load
  if ((!initialLoadComplete || !objectCountsReady) && !error) {
    return <CustomersSkeleton />;
  }
  if (error && !initialLoadComplete) {
    return (
      <ListPageShell kicker={t('customers:kicker')} title={t('customers:title')}>
        <EmptyState
          variant="error"
          title={t('customers:messages.loadError')}
          description={error || t('common:errors.tryAgain')}
          onRetry={() => fetchAllCustomers()}
        />
      </ListPageShell>
    );
  }

  return (
    <div>
      <ListPageShell
        kicker={t('customers:kicker')}
        title={t('customers:title')}
        meta={t('customers:meta', { count: customers.length, objects: totalObjects })}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => exportExcel(exportIds)}
              loading={exporting}
              leftIcon={<Download className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {exporting
                ? t('common:excelImport.exporting')
                : exportIds
                  ? t('common:excelImport.exportFiltered', { total: exportIds.length })
                  : t('customers:actions.exportExcel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsImportModalOpen(true)}
              disabled={exporting}
              leftIcon={<Upload className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('customers:actions.importExcel')}
            </Button>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('customers:newCustomer')}
            </Button>
          </>
        }
      >
        <KpiStripList items={kpis} />
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-3 sm:px-4">
            <TabStrip
              tabs={tabs}
              value={tab}
              onChange={(id) => setListState({ tab: id as TabId })}
              ariaLabel={t('customers:title')}
            />
          </div>
          <ListToolbar
            placeholder={t('customers:placeholders.search')}
            search={search}
            onSearchChange={(value) => setListState({ q: value })}
            sortValue={sortBy}
            onSortChange={(id) => setListState({ sort: id as SortId })}
            sortOptions={sortOptions}
            className="px-3 sm:px-4 py-2.5"
          />
        </div>

        {filteredCustomers.length === 0 ? (
          <EmptyState
            icon={<Users className="w-12 h-12" />}
            title={
              search || tab !== 'all'
                ? t('customers:empty.titleFiltered')
                : t('customers:empty.title')
            }
            description={
              search || tab !== 'all'
                ? t('customers:empty.descriptionFiltered')
                : t('customers:empty.description')
            }
            action={
              !search && tab === 'all' ? (
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
                >
                  {t('customers:actions.create')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Desktop: ListGrid */}
            <div className="hidden md:block">
              <ListGrid columns={GRID_COLUMNS} ariaLabel={t('customers:title')}>
                <ListGrid.Header>
                  <span>{t('customers:fields.customerNumber')}</span>
                  <span>{t('customers:fields.name')}</span>
                  <span>{t('customers:fields.city')}</span>
                  <span className="text-right">{t('customers:sections.objects')}</span>
                  <span className="text-right">{t('customers:fields.status')}</span>
                  <span />
                  <span />
                </ListGrid.Header>
                {pageItems.map((customer) => {
                  const objCount = objectCountByCustomer.get(customer.id ?? '') ?? 0;
                  const status = mapListStatus(customer.status);
                  return (
                    <ListGrid.Row
                      key={customer.id}
                      onClick={() => customer.id && navigate(`/customers/${customer.id}`)}
                    >
                      <div className="px-3 py-3 font-mono-num text-[11.5px] text-slate-500">
                        {customer.customerNumber ?? '—'}
                      </div>
                      <div className="px-3 py-3 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate">
                            {customer.name ?? '—'}
                          </span>
                        </div>
                      </div>
                      <div className="px-3 py-3 text-slate-600 truncate">
                        {customer.city ?? '—'}
                      </div>
                      <div className="px-3 py-3 text-right font-mono-num text-slate-900">
                        {objCount > 0 ? objCount : '—'}
                      </div>
                      <div className="px-3 py-3 flex items-center justify-end">
                        <StatusDotInline status={status} />
                      </div>
                      <div className="px-1 py-3 flex items-center justify-end">
                        <RowAction
                          onView={() => customer.id && navigate(`/customers/${customer.id}`)}
                          onEdit={() => openEditModal(customer)}
                          onDelete={() => openDeleteModal(customer)}
                          t={t}
                        />
                      </div>
                      <div />
                    </ListGrid.Row>
                  );
                })}
              </ListGrid>
            </div>

            {/* Mobile: Card stack */}
            <div className="md:hidden space-y-2">
              {pageItems.map((customer) => {
                const objCount = objectCountByCustomer.get(customer.id ?? '') ?? 0;
                const status = mapListStatus(customer.status);
                return (
                  <div
                    key={customer.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => customer.id && navigate(`/customers/${customer.id}`)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && customer.id) {
                        e.preventDefault();
                        navigate(`/customers/${customer.id}`);
                      }
                    }}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 active:bg-slate-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00A871]/40"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 text-[14px] truncate">
                          {customer.name ?? '—'}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate font-mono-num">
                          {customer.customerNumber ?? '—'}
                          <span className="mx-1 text-slate-400">·</span>
                          {customer.city ?? '—'}
                        </div>
                      </div>
                      <StatusDotInline status={status} />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                      <span className="font-mono-num">
                        {objCount > 0
                          ? `${objCount} ${t('customers:kpis.objectsTotal')}`
                          : t('customers:tabs.noObjects')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <Pagination
              page={page}
              pageCount={pageCount}
              totalCount={filteredCustomers.length}
              onPageChange={goToPage}
              disabled={loading}
            />
          </>
        )}
      </ListPageShell>

      {/* Excel round-trip: upload the edited export, preview, then apply */}
      <ExcelImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        entity="customers"
        title={t('customers:actions.importTitle')}
        onImported={() => fetchAllCustomers()}
      />

      {/* Create SlideOver */}
      <SlideOver
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          createForm.reset();
        }}
        title={t('customers:create')}
        size="xl"
        hasUnsavedChanges={createForm.isDirty}
        footer={
          <SlideOverFooter
            onCancel={() => {
              setIsCreateModalOpen(false);
              createForm.reset();
            }}
            onConfirm={handleCreate}
            confirmText={t('customers:actions.create')}
            loading={saving}
          />
        }
      >
        <CustomerForm form={createForm} mode="create" t={t} />
      </SlideOver>

      {/* Edit SlideOver */}
      <SlideOver
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCustomer(null);
          editForm.reset();
        }}
        title={t('customers:editCustomer')}
        size="xl"
        hasUnsavedChanges={editForm.isDirty}
        footer={
          <SlideOverFooter
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedCustomer(null);
              editForm.reset();
            }}
            onConfirm={handleEdit}
            confirmText={t('common:actions.save')}
            loading={saving}
          />
        }
      >
        <CustomerForm form={editForm} mode="edit" t={t} />
      </SlideOver>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCustomer(null);
        }}
        title={t('customers:deleteCustomer')}
        size="sm"
        footer={
          <ModalFooter
            onCancel={() => {
              setIsDeleteModalOpen(false);
              setSelectedCustomer(null);
            }}
            onConfirm={handleDelete}
            confirmText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            variant="danger"
          />
        }
      >
        <p className="text-[13px] text-slate-700">
          {t('customers:confirmDelete', { name: selectedCustomer?.name || '' })}
        </p>
      </Modal>
    </div>
  );
}

function RowAction({
  onView,
  onEdit,
  onDelete,
  t,
}: {
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string) => string;
}) {
  return (
    <div
      className="opacity-60 group-hover:opacity-100 group-focus-within:opacity-100 flex items-center gap-0.5 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onView}
        title={t('customers:actions.viewDetails')}
        aria-label={t('customers:actions.viewDetails')}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Eye className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onEdit}
        title={t('customers:actions.edit')}
        aria-label={t('customers:actions.edit')}
        className="w-7 h-7 grid place-items-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
      >
        <Pencil className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDelete}
        title={t('customers:actions.delete')}
        aria-label={t('customers:actions.delete')}
        className="w-7 h-7 grid place-items-center rounded text-red-600 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}

interface CustomerFormProps {
  form: ReturnType<typeof useFormValidation<CustomerFormData>>;
  mode: 'create' | 'edit';
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function CustomerForm({ form, mode, t }: CustomerFormProps) {
  return (
    <div className="space-y-4">
      <FormField
        label={t('customers:fields.name')}
        required
        name="name"
        value={form.values.name}
        onChange={form.handleInputChange}
        onBlur={form.handleInputBlur}
        error={form.touched.name ? form.errors.name : undefined}
      />
      {mode === 'create' ? (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
          <p className="text-[12px] text-sky-800">
            {t('customers:messages.customerNumberAuto')}
          </p>
        </div>
      ) : (
        <FormField
          label={t('customers:fields.customerNumber')}
          name="customerNumber"
          value={form.values.customerNumber}
          disabled
          helperText={t('customers:messages.customerNumberReadonly')}
        />
      )}
      <FormField
        label={t('customers:fields.street')}
        required
        name="street"
        value={form.values.street}
        onChange={form.handleInputChange}
        onBlur={form.handleInputBlur}
        error={form.touched.street ? form.errors.street : undefined}
      />
      <div className="grid grid-cols-2 gap-4">
        <FormField
          label={t('customers:fields.postalCode')}
          required
          name="postalCode"
          value={form.values.postalCode}
          onChange={form.handleInputChange}
          onBlur={form.handleInputBlur}
          error={form.touched.postalCode ? form.errors.postalCode : undefined}
        />
        <FormField
          label={t('customers:fields.city')}
          required
          name="city"
          value={form.values.city}
          onChange={form.handleInputChange}
          onBlur={form.handleInputBlur}
          error={form.touched.city ? form.errors.city : undefined}
        />
      </div>
      <FormField
        label={t('customers:fields.country')}
        name="country"
        value={form.values.country}
        onChange={form.handleInputChange}
      />
      <FormField
        label={t('customers:fields.vatId')}
        name="vatId"
        value={form.values.vatId}
        onChange={form.handleInputChange}
        maxLength={32}
      />
      <TextArea
        label={t('customers:fields.externalNotes')}
        helperText={t('customers:externalNotesHelper')}
        name="externalNotes"
        value={form.values.externalNotes}
        onChange={form.handleInputChange}
        rows={3}
      />
      <TextArea
        label={t('customers:fields.internalNotes')}
        helperText={t('customers:internalNotesHelper')}
        name="internalNotes"
        value={form.values.internalNotes}
        onChange={form.handleInputChange}
        rows={3}
      />
    </div>
  );
}

function CustomersSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 animate-pulse">
      <div className="h-[88px] rounded-xl border border-slate-200 bg-white" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl border border-slate-200 bg-white" />
        ))}
      </div>
      <div className="h-10 rounded-md bg-slate-100" />
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-14 border-t border-slate-100 first:border-t-0 bg-slate-50/50" />
        ))}
      </div>
    </div>
  );
}
